.outline {
    /* Do not use if the thing isn't using react-aria somehow */
    :where(&[data-focused]) {
        outline: none;
    }

    :where(&[data-focus-visible="true"]) {
        outline: 1px solid var(--black);
        outline-offset: 2px;
    }
}

/**
 * Should only be used if <Clickable> is unusable for some reason.
 */
.buttonReset {
    cursor: pointer;
}

:where(.buttonReset) {
    display: inline-block;
    appearance: none;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: 1em;
    line-height: inherit;
    text-decoration: none;

    &:disabled,
    &[data-disabled] {
        opacity: 0.5;
        pointer-events: none;
    }
}

:where(.linkReset) {
    cursor: pointer;
    display: inline-block;
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    text-decoration: none;
}

.base {
    composes: outline;
    composes: buttonReset;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 1em;
        height: 1em;
        margin-left: var(--spacer-8);
        fill: currentColor;
        flex-shrink: 0;
    }
}

.buttonPrimary {
    composes: base;
    font: var(--font-button-20);
    white-space: nowrap;
    background: var(--red);
    color: var(--white);
    padding: var(--spacer-16) var(--spacer-32);
    border: 1px solid var(--red);
    border-radius: var(--border-radius-full);

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            background: var(--white);
            color: var(--red);
        }
    }
}

.buttonSecondary {
    composes: base;
    font: var(--font-button-16);
    white-space: nowrap;
    background: var(--white);
    color: var(--red);
    padding: var(--spacer-12) var(--spacer-24);
    border: 1px solid var(--red);
    border-radius: var(--border-radius-full);

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            background: var(--red);
            color: var(--white);
        }
    }
}

.buttonTertiary {
    composes: base;
    white-space: nowrap;
    font: var(--font-button-16);
    background: var(--black);
    color: var(--white);
    padding-inline: var(--spacer-32);
    height: 32px;
    border-radius: var(--border-radius-full);

    &:has(> svg) {
        padding-right: var(--spacer-24);
    }

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            background: var(--red);
        }
    }
}

.buttonTextPrimary {
    composes: base;
    white-space: nowrap;
    font: var(--font-button-20);
    color: var(--color-contrast);

    &[data-hovered] svg {
        transform: translateX(4px);
    }
}

.buttonTextSecondary {
    composes: base;
    white-space: nowrap;
    font: var(--font-button-16);
    color: var(--color-contrast);

    &[data-hovered] svg {
        transform: translateX(4px);
    }
}

.textLink {
    composes: base;
    text-decoration: underline;
    text-underline-offset: 0.1875em;
    display: inline;
}

.iconButton {
    composes: base;
    border-radius: var(--border-radius-full);
    aspect-ratio: 1;
    padding: var(--spacer-12);
    line-height: 0;

    & svg {
        margin-left: 0;
    }

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            background: var(--red);
            border-color: var(--red);
            color: var(--white);
        }
    }
}

.iconButtonOutline {
    composes: iconButton;
    border: 2px solid var(--black);
}

.iconButtonFilled {
    composes: iconButton;
    background: var(--transparent-20);
    color: var(--white);
}

.pill {
    composes: base;
    padding: var(--spacer-8) var(--spacer-16);
    border: 1px solid var(--black);
    border-radius: var(--border-radius-full);

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            background: var(--mid-gray);
        }
    }

    &[data-selected] {
        background: var(--black);
        color: var(--white);
    }
}

.pillGroup {
    display: inline-flex;
    gap: var(--spacer-12);
    flex-wrap: wrap;
}

.longButton {
    composes: base;
    display: grid;
    justify-content: space-between;
    width: 100%;
    outline-offset: 0;
    outline-color: currentColor;
    transition: outline-offset var(--transition);

    --transition: 0.3s ease;

    svg {
        transition: transform var(--transition);
        margin-right: 8px;
    }

    [role="separator"] {
        grid-column: span 2;

        --color: currentColor;
    }

    &:hover,
    &[data-hovered] {
        outline-offset: 8px;

        svg {
            transform: translateX(8px);
        }
    }
}

.longButtonPrimary {
    composes: longButton;
    composes: fontHB32 from "../../styles/typography.module.scss";
    row-gap: var(--spacer-24);
}

.longButtonSecondary {
    composes: fontHB24 from "../../styles/typography.module.scss";
    composes: longButton;
    row-gap: var(--spacer-16);
}
